<script>
	import { onMount, onDestroy } from "svelte";
	import Button from "../windows/Button.svelte";
	import journey from "../../stores/journey.js";
	import cairns from "../../stores/cairn.js";
	import semaphore from "../../stores/semaphore.js";
	import { buildCairn, placeCairn } from "../../api/api.js";
	import windows from "../../stores/windows";
	import chronology from "../../stores/chronology";
	import journal from "../../stores/journal";

	const MINIMUM_CAIRN_SIZE = 1;
	const MAXIMUM_CAIRN_SIZE = 10;
	const MAXIMUM_NAME_SIZE = 25;
	const MAXIMUM_MESSAGE_SIZE = 100;

	export let window;

	const seedBase = hash(Math.random());

	let size = MINIMUM_CAIRN_SIZE;
	let name = $cairns.name || "";
	let message = "";
	let cairn;
	let isPlacingCairn = false;
	let isLoadingCairn = false;

	$: nameLengthCounterColor = name.length > MAXIMUM_NAME_SIZE ? "#cc3300" : "#000000";

	$: messageLengthCounterColor = message.length > MAXIMUM_MESSAGE_SIZE ? "#cc3300" : "#000000";

	$: canSubmit = name.length > 0 && name.length <= MAXIMUM_NAME_SIZE && isPlacingCairn === false;

	function hash(number) {
		return number.toString(32).substr(2, 5);
	}

	function seed() {
		if ($journey?.location === undefined) {
			return seedBase;
		}

		return seedBase + hash($journey.location.latitude) + hash($journey.location.longitude);
	}

	async function updateCairn() {
		isLoadingCairn = true;

		try {
			cairn = await buildCairn(size, seed());
		} catch (error) {
			console.warn(error);
		}

		isLoadingCairn = false;
	}

	function didClickDecreaseButton() {
		size = Math.max(MINIMUM_CAIRN_SIZE, size - 1);
		updateCairn();
	}

	function didClickIncreaseButton() {
		size = Math.min(MAXIMUM_CAIRN_SIZE, size + 1);
		updateCairn();
	}

	async function didClickBuildButton() {
		isPlacingCairn = true;

		try {
			if (!$journey.location) {
				throw new Error("Location not available");
			}

			cairn = await placeCairn(
				size,
				name,
				seed(),
				message,
				$journey.location.latitude,
				$journey.location.longitude
			);

			journal.remember(`built a cairn using ${size} rocks`);

			cairns.add(cairn, $chronology.duration, name);

			windows.close(window.id);
		} catch (error) {
			console.warn(error);
		}

		isPlacingCairn = false;
	}

	onMount(() => {
		semaphore.pause("Picking up rocks to build a cairn.");
		updateCairn();
	});

	onDestroy(() => {
		semaphore.resume();
	});
</script>

<div class="build-cairn">
	<div class="preview">
		<div class="flexible" />
		{#if cairn}
			<img
				alt="Stack of stones"
				class:loading={isLoadingCairn}
				src={`data:image/svg+xml,${encodeURIComponent(cairn.side)}`} />
		{/if}
		<fieldset>
			<button on:click={didClickDecreaseButton} disabled={size === MINIMUM_CAIRN_SIZE}>–</button>
			<input class="size" bind:value={size} readonly />
			<button on:click={didClickIncreaseButton} disabled={size === MAXIMUM_CAIRN_SIZE}>+</button>
		</fieldset>
	</div>
	<div class="form">
		<fieldset>
			<input type="text" placeholder="Your name" bind:value={name} />
			<span style={`color: ${nameLengthCounterColor}`}>
				{name.length} / {MAXIMUM_NAME_SIZE}
			</span>
		</fieldset>
		<fieldset>
			<textarea placeholder="Note (optional)" rows="5" bind:value={message} />
			<span style={`color: ${messageLengthCounterColor}`}>
				{message.length} / {MAXIMUM_MESSAGE_SIZE}
			</span>
		</fieldset>
		<div class="flexible" />
		<div class="navigation">
			<div class="flexible" />
			<Button isEnabled={canSubmit} action={didClickBuildButton}>Build</Button>
		</div>
	</div>
</div>

<style>
	.build-cairn {
		display: flex;
		gap: calc(var(--spacing) * 2);
		box-sizing: border-box;
		padding: calc(var(--spacing) * 2);
		height: 100%;
	}

	.preview {
		display: flex;
		flex-direction: column;
		justify-content: flex-end;
		align-items: center;
		width: 50%;
	}

	img {
		margin-bottom: calc(var(--spacing) * 2);
		width: auto;
		max-width: 100%;
		max-height: calc(100% - var(--spacing) * 6);
		object-fit: contain;
	}

	img.loading {
		opacity: 0.25;
	}

	.form {
		display: flex;
		flex-direction: column;
		align-items: flex-end;
		gap: var(--spacing);
		width: 50%;
		height: 100%;
	}

	.flexible {
		flex-grow: 1;
	}

	fieldset {
		margin: 0;
		border: none;
		padding: 0;
	}

	.form fieldset {
		width: 100%;
	}

	input.size {
		margin: 0;
		padding: 2px var(--spacing);
		width: 30px;
		text-align: center;
	}

	input,
	textarea {
		box-sizing: border-box;
		margin-bottom: var(--spacing);
		border-top: var(--border-color-dark) var(--chrome-depth) inset;
		border-right: var(--border-color-light) var(--chrome-depth) inset;
		border-bottom: var(--border-color-light) var(--chrome-depth) inset;
		border-left: var(--border-color-dark) var(--chrome-depth) inset;
		padding: var(--spacing);
		width: 100%;
	}

	textarea {
		margin-top: var(--spacing);
		resize: none;
	}

	button {
		appearance: none;
		box-sizing: border-box;
		margin: 0;
		border-top: var(--border-color-light) var(--chrome-depth) outset;
		border-right: var(--border-color-dark) var(--chrome-depth) outset;
		border-bottom: var(--border-color-dark) var(--chrome-depth) outset;
		border-left: var(--border-color-light) var(--chrome-depth) outset;
		background: var(--chrome-hsl);
		padding: 3px 6px 4px 6px;
		width: 30px;
		color: #000000;
		line-height: 100%;
	}

	button:disabled {
		color: #999999;
		text-shadow: 0 1px rgba(255, 255, 255, 0.75);
	}

	.navigation {
		display: flex;
		align-items: center;
		width: 100%;
	}
</style>
